import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSurveyPaths } from '../../../hooks/useSurveyPaths';
import { Link } from 'react-router-dom';
import { Heading, Link as DsLink } from '@digdir/designsystemet-react';
import type { ActionPlan, PlanArea } from '../../../api/workEnvironmentTypes';
import { longDate } from '../../Settings/privacyView';
import { SavedText } from '../components/FormParts';
import { goalFor } from '../planView';
import { areaName } from '../riskView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from './FollowUp.module.css';

type Props = {
  surveyId: number;
  area: PlanArea;
  plan: ActionPlan;
  canChange: boolean;
  onEdit: () => void;
};

const measuringKey = (plan: ActionPlan) =>
  plan.measurePulse && plan.measureSafety
    ? 'both'
    : plan.measurePulse
      ? 'pulse'
      : plan.measureSafety
        ? 'safety'
        : null;

export const PlanFactsCard = ({
  surveyId,
  area,
  plan,
  canChange,
  onEdit,
}: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const paths = useSurveyPaths();
  const measuring = measuringKey(plan);
  return (
    <section
      aria-labelledby="follow-up-plan-heading"
      className={`${process.card} ${process.sideCard}`}
    >
      <Heading level={2} data-size="xs" id="follow-up-plan-heading">
        {t('workEnvironmentFollowUp.plan.title')}
      </Heading>
      <SavedText
        title={t('workEnvironmentFollowUp.plan.area')}
        text={t('workEnvironmentFollowUp.plan.areaValue', {
          name: areaName(area, t),
          level: t(
            `workEnvironmentRisk.matrix.levels.${area.level}`,
          ).toLowerCase(),
        })}
      />
      {goalFor(plan, area.key) && (
        <SavedText
          title={t('workEnvironmentFollowUp.plan.goal')}
          text={goalFor(plan, area.key) ?? ''}
        />
      )}
      {plan.frequency && (
        <SavedText
          title={t('workEnvironmentFollowUp.plan.followUp')}
          text={[
            t('workEnvironmentFollowUp.plan.followUpValue', {
              every: t(`workEnvironmentFollowUp.every.${plan.frequency}`),
            }),
            measuring &&
              t(`workEnvironmentFollowUp.plan.measuring.${measuring}`),
          ]
            .filter(Boolean)
            .join(' ')}
        />
      )}
      {plan.evaluationDate && (
        <SavedText
          title={t('workEnvironmentFollowUp.plan.evaluation')}
          text={t('workEnvironmentFollowUp.plan.evaluationValue', {
            date: longDate(plan.evaluationDate, i18n.language),
          })}
        />
      )}
      <div className={classes.links}>
        {canChange && (
          <DsLink asChild>
            <button type="button" onClick={onEdit}>
              {t('workEnvironmentFollowUp.plan.edit')}
            </button>
          </DsLink>
        )}
        <Link to={paths.report(surveyId)} className="ds-link">
          {t('workEnvironmentFollowUp.plan.report')}
        </Link>
      </div>
    </section>
  );
};
